import { api } from "../lib/api";

// Type definitions
export interface Dimension {
  id: number;
  label: string;
  short_label?: string;
  description?: string;
  color_hex: string;
  rubric_level_1?: string;
  rubric_level_2?: string;
  rubric_level_3?: string;
  is_active: boolean;
}

export interface DimensionScore {
  personal: number;
  classAvg: number;
  ai_score?: number;
  ai_reasoning?: string;
  ai_strengths?: string[];
  ai_improvements?: string[];
  ai_examples?: string;
  instructor_override_score?: number;
  instructor_comment?: string;
}

export interface Submission {
  id: string;
  name: string;
  date: string;
  scores: Record<string, DimensionScore>;
  ai_processing_status?: "pending" | "processing" | "completed" | "failed";
  ai_overall_summary?: string;
  ai_overall_strengths?: string[];
  ai_priority_improvements?: string[];
}

export interface Project {
  id: string;
  name: string;
  date: string;
  summary: string;
}

export interface ProjectWithSubmissions {
  project: Project;
  submissions: Submission[];
}

export interface ProjectListItem {
  id: string;
  name: string;
  summary: string;
  date: string;
  submission_count: number;
}

// API response types
interface DimensionsResponse {
  dimensions: Dimension[];
}

interface ProjectSubmissionsResponse {
  project: Project | null;
  submissions: Submission[];
}

interface ProjectsResponse {
  projects: ProjectListItem[];
}

// Service functions
export const dimensionsService = {
  /**
   * Get all dimensions (static catalog data)
   */
  async getDimensions(): Promise<Dimension[]> {
    const data = await api.get<DimensionsResponse>("/dimensions");
    return data.dimensions;
  },

  /**
   * Get all submissions with scores for a specific project
   */
  async getProjectSubmissions(
    projectId: string
  ): Promise<ProjectWithSubmissions> {
    const data = await api.get<ProjectSubmissionsResponse>(
      `/dimensions/projects/${projectId}/submissions`
    );

    // Return empty structure if no project found
    if (!data.project) {
      return {
        project: {
          id: projectId,
          name: "",
          date: new Date().toISOString(),
          summary: "",
        },
        submissions: [],
      };
    }

    return {
      project: data.project,
      submissions: data.submissions,
    };
  },

  /**
   * Get all projects for the current user
   */
  async getProjects(): Promise<ProjectListItem[]> {
    const data = await api.get<ProjectsResponse>("/dimensions/projects");
    return data.projects;
  },
};
